import {
  Fragment,
  useCallback,
  useState,
  useRef,
  useEffect,
  KeyboardEvent,
} from 'react';
import { navigateWorksheetTab } from '../../shared/utils/tabNavigation';
import DateValue from '../DateValue/DateValue';
import { CalculatedIndicator } from '../../Measurements/Row/CalculatedIndicator';
import styles from './ContextRow.module.css';

interface ContextRowProps {
  label: string;
  value: string;
  unit?: string;
  /** Formula behind a calculated row (BSA) — shows `fx` on the label. */
  formula?: string;
  /** The value no longer matches its formula — shows `≠` after `fx`. */
  diverged?: boolean;
  /** Formula result at display precision; null when it cannot be evaluated. */
  calculatedText?: string | null;
  /** Operand keys the formula could not resolve. */
  missingInputs?: string[];
  /** Ctrl/Cmd+click on `fx` or `≠` resets the row to its calculated value. */
  onConverge?: () => void;
  /** Differs from the saved report — amber bar right of the input. */
  unsaved?: boolean;
  editable: boolean;
  onChange?: (value: string) => void;
  disabled?: boolean;
  /** A plain date ("YYYY-MM-DD"): shown as "Jan 10, 1980", edited with a calendar. */
  isDate?: boolean;
  /** Split value (imperial height): one box per part, each with its own unit. */
  parts?: { value: string; unit: string }[];
  /** Commits the split boxes as one value, in `parts` order. */
  onPartsChange?: (values: string[]) => void;
}

export default function ContextRow({
  label,
  value,
  unit,
  formula,
  diverged = false,
  calculatedText = null,
  missingInputs,
  onConverge,
  unsaved = false,
  editable,
  onChange,
  disabled = false,
  isDate = false,
  parts,
  onPartsChange,
}: ContextRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const isValueEditable = editable && !!onChange && !disabled;

  const handleClick = useCallback(() => {
    if (!isValueEditable) return;
    setEditValue(value);
    setIsEditing(true);
  }, [isValueEditable, value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    if (editValue !== value && onChange) {
      onChange(editValue);
    }
  }, [editValue, value, onChange]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      navigateWorksheetTab(e.currentTarget, e.shiftKey);
      return;
    }
    if (e.key === 'Enter' || e.key === 'Escape') {
      e.currentTarget.blur();
    }
  }, []);

  // Split boxes: edited together, committed on the blur of whichever box the
  // reader leaves, against the values the row showed when editing began.
  const [partEdits, setPartEdits] = useState<string[] | null>(null);
  const partsEditable = isValueEditable && !!onPartsChange;
  const handlePartFocus = useCallback(() => {
    if (!partsEditable || !parts) return;
    setPartEdits((prev) => prev ?? parts.map((p) => p.value));
  }, [partsEditable, parts]);
  const handlePartBlur = useCallback(() => {
    if (!partEdits || !parts) return;
    setPartEdits(null);
    if (partEdits.some((v, i) => v !== parts[i].value))
      onPartsChange?.(partEdits);
  }, [partEdits, parts, onPartsChange]);

  const isEmpty = !value || value.trim() === '';

  return (
    <div className={`${styles.row} ${unsaved ? styles.rowUnsaved : ''}`}>
      <span className={styles.label}>
        {label}
        {formula && (
          <CalculatedIndicator
            formula={formula}
            diverged={diverged}
            calculatedText={calculatedText}
            enteredText={value}
            missingInputs={missingInputs}
            onConverge={onConverge}
            disabled={disabled}
          />
        )}
      </span>

      <div className={styles.valueContainer}>
        {parts && (
          // Boxes and the units between them are grid cells, so the two boxes
          // share the width equally whatever the unit label takes. The last
          // part's unit takes the row's unit column, so the last box ends
          // where every other input ends.
          <div className={styles.parts}>
            {parts.map((part, i) => (
              <Fragment key={part.unit}>
                <span className={styles.valueCell}>
                  <input
                    type="text"
                    className={`${styles.value} ${partEdits ? styles.valueEditing : styles.valueDisplay}`}
                    value={partEdits ? partEdits[i] : part.value}
                    data-committed={part.value}
                    placeholder="-"
                    readOnly={!partsEditable}
                    onFocus={handlePartFocus}
                    onChange={(e) =>
                      setPartEdits((prev) =>
                        prev
                          ? prev.map((v, j) => (j === i ? e.target.value : v))
                          : prev
                      )
                    }
                    onBlur={handlePartBlur}
                    onKeyDown={handleKeyDown}
                  />
                  <span className={styles.unsavedBar} aria-hidden="true" />
                </span>
                {i < parts.length - 1 && (
                  <span className={styles.partUnit}>{part.unit}</span>
                )}
              </Fragment>
            ))}
          </div>
        )}
        {!parts && isDate && (
          <span className={styles.valueCell}>
            <DateValue
              label={label}
              value={value}
              editable={isValueEditable}
              onChange={onChange}
            />
            <span className={styles.unsavedBar} aria-hidden="true" />
          </span>
        )}
        {!parts &&
          !isDate &&
          (isValueEditable ? (
            <span className={styles.valueCell}>
              <input
                ref={inputRef}
                type="text"
                className={`${styles.value} ${isEditing ? styles.valueEditing : styles.valueDisplay} ${isEmpty && !isEditing ? styles.valueEmpty : ''}`}
                value={isEditing ? editValue : value}
                data-committed={value}
                placeholder="-"
                readOnly={!isEditing}
                onFocus={!isEditing ? handleClick : undefined}
                onClick={!isEditing ? handleClick : undefined}
                onChange={
                  isEditing ? (e) => setEditValue(e.target.value) : undefined
                }
                onBlur={isEditing ? handleBlur : undefined}
                onKeyDown={handleKeyDown}
              />
              <span className={styles.unsavedBar} aria-hidden="true" />
            </span>
          ) : (
            // Same cell and bar slot as an input, so the text lines up with
            // the editable rows above and below.
            <span className={styles.valueCell}>
              <span
                className={`${styles.value} ${isEmpty ? styles.valueEmpty : ''}`}
              >
                {value || '-'}
              </span>
              <span className={styles.unsavedBar} aria-hidden="true" />
            </span>
          ))}
      </div>

      {/* Column 3: unit */}
      <span className={styles.unit}>
        {parts ? parts[parts.length - 1].unit : (unit ?? '')}
      </span>
    </div>
  );
}
